import * as THREE from 'three'
import { Vec3 } from '../core/math/vec3'

/**
 * Converte una posizione mondo (doppia precisione, fino a milioni di metri)
 * in coordinate di scena relative all'origine flottante.
 *
 * E la funzione che tiene insieme tutto il rendering: nessun oggetto riceve
 * mai una coordinata mondo assoluta, perche i float a 32 bit della GPU a
 * 600 km dal centro avrebbero una precisione di 6 centimetri — e il razzo e
 * lungo 24 metri.
 */
export const toScene = (world: Vec3, origin: Vec3): THREE.Vector3 =>
  new THREE.Vector3(world.x - origin.x, world.y - origin.y, world.z - origin.z)

export interface Renderer {
  readonly renderer: THREE.WebGLRenderer
  readonly camera: THREE.PerspectiveCamera
  resize(): void
  render(scene: THREE.Scene): void
}

export function createRenderer(canvas: HTMLCanvasElement): Renderer {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    // un razzo da 24 m e un pianeta da 600 km nella stessa scena: senza
    // profondita logaritmica sarebbe tutto z-fighting
    logarithmicDepthBuffer: true,
    // serve al collaudo, che rilegge i pixel dopo il disegno
    preserveDrawingBuffer: true,
  })
  renderer.setClearColor(0x03050a, 1)
  renderer.outputColorSpace = THREE.SRGBColorSpace

  const camera = new THREE.PerspectiveCamera(58, 1, 0.1, 5e9)

  function resize(): void {
    const w = Math.max(1, window.innerWidth)
    const h = Math.max(1, window.innerHeight)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    renderer.setSize(w, h, false)
    camera.aspect = w / h
    camera.updateProjectionMatrix()
  }

  resize()
  window.addEventListener('resize', resize)

  return {
    renderer,
    camera,
    resize,
    render: (scene: THREE.Scene) => renderer.render(scene, camera),
  }
}
